import React, { useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Button, Card, CardActions, CardContent, Container, CssBaseline, InputLabel, MenuItem, Paper, Select, TextField } from '@material-ui/core';
import MerchantLayout from '../../layout/MerchantLayout';
import { MerchantSignupT } from '../../types/merchant';
import { createMerchant } from '../../services/c2dcrm';
import { useAuth } from '../../hooks/useAuth';
import { useModalContext } from '../../components/Context/ModalContext';

const useStyles = makeStyles({
  root: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    display: 'flex',
    placeItems: 'center',
  },
  header: {
    display: 'grid',
    gridGap: '16px',
    marginBottom: '40px',
    justifyContent: 'center',
  },
  sectionHeader: {
    display: 'grid',
    gridGap: '16px',
    marginTop: '10px',
  },
  emailWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '40px',
  },
});

const MerchantCreateProfilePage: React.FC = () => {
  const { token } = useAuth();
  const classes = useStyles();
  const { register, handleSubmit } = useForm<MerchantSignupT>();
  const [focus, setFocus] = useState<string | number>('');
  const [intensity, setIntensity] = useState<string | number>('');
  const [cost, setCost] = useState<string | number>('');
  const [duration, setDuration] = useState<string | number>('');
  const [qualification, setQualification] = useState<string>('');
  const { openModal } = useModalContext();

  const onSubmit: SubmitHandler<MerchantSignupT> = (data: MerchantSignupT) => {
    data.metadata = JSON.stringify({
      program: {
        focus: focus,
        intensity: intensity,
        duration: duration,
        cost: cost,
        qualification: qualification,
      }
    });

    createMerchant(token, data).then((response) => {
      if (response.status === 200) {
        // Alert "thank you for signing up INFO page"
        openModal({
          open: true,
          component: FinishMerchantSignup,
          componentProps: {
            closeModalWindow: () => {
              window.location.replace('/merchant');
            }
          }
        });
      }
    })
  };

  const handleFocusChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFocus(event.target.value as string);
  };

  const handleIntensityChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setIntensity(event.target.value as number);
  };

  const handleDurationChange = (event: React.ChangeEvent<{ value: string }>) => {
    setDuration(event.target.value);
  };

  const handleCostChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setCost(event.target.value as number);
  };

  const handleQualificationChange = (event: React.ChangeEvent<{ value: string }>) => {
    setQualification(event.target.value);
  };

  return (
    <Container component="main" maxWidth="sm">
      <MerchantLayout>
        <CssBaseline />
        <Paper style={{ padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <p className={classes.header}>
            <Typography variant="h1" color="primary">
              Complete your profile
            </Typography>
          </p>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={classes.sectionHeader}>
              <Typography variant="h1" color="primary">
                Company Information
              </Typography>
            </div>

              <TextField
                key="1"
                id="name"
                style={{ width: '100%'}}
                label="Name"
                defaultValue=''
                {...register('company_name', { required: true })}
              />
            <TextField
              key="2"
              type="text"
              style={{ width: '100%'}}
              label="Registration Number"
              {...register('company_registration', { required: true })}
            />
            <TextField
              style={{ width: '100%'}}
              key="3"
              type="text"
              label="City"
              {...register('city', { required: true })}
            />
            <TextField
              key="4"
              style={{ width: '100%'}}
              type="text"
              label="State"
              {...register('state', { required: true })}
            />
            <TextField
              key="5"
              style={{ width: '100%'}}
              type="text"
              label="Country"
              {...register('country', { required: true })}
            />
            <TextField
              key="6"
              style={{ width: '100%'}}
              type="text"
              label="Postal Code"
              {...register('postal_code', { required: true })}
            />
            <div className={classes.sectionHeader}>
              <Typography
                variant="h1"
                color="primary"
                style={{ marginTop: '20px'}}
              >
                Program Information
              </Typography>
            </div>
            <InputLabel id="program-focus-label">Focus</InputLabel>
            <Select
              onChange={handleFocusChange}
              labelId="program-focus-label"
              id="program-focus-select"
              style={{ width: '100%' }}
            >
              <MenuItem value={"exercise"}>Exercise</MenuItem>
              <MenuItem value={"nutrition"}>Nutrition</MenuItem>
              <MenuItem value={"nutrition-exercise"}>Both Nutrition and Exercise</MenuItem>
            </Select>
            <InputLabel id="program-intensity-label">Intensity</InputLabel>
            <Select
              onChange={handleIntensityChange}
              labelId="program-intensity-label"
              id="program-intensity-select"
              style={{ width: '100%' }}
            >
              <MenuItem value={1}>1 - Mild</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3 - Moderate</MenuItem>
              <MenuItem value={4}>4</MenuItem>
              <MenuItem value={5}>5 - Intense</MenuItem>
            </Select>
            <TextField
              key="7"
              type="text"
              label="Duration"
              style={{ width: '100%'}}
              onChange={handleDurationChange}
            />
            <TextField
              key="8"
              type="text"
              label="Cost"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              style={{ width: '100%'}}
              onChange={handleCostChange}
            />
            <TextField
              key="9"
              type="text"
              label="Qualifications"
              style={{ width: '100%'}}
              onChange={handleQualificationChange}
            />

            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{ marginTop: '20px', width: '100%'}}
            >
              Sign up
            </Button>
          </form>
        </Paper>
      </MerchantLayout>
    </Container>
  );
};

export default MerchantCreateProfilePage;

type MProps = {
  closeModalWindow: () => void;
}


export const FinishMerchantSignup: React.FC<MProps> = ({ closeModalWindow }) => {
  return (
    <Card>
      <CardContent>
        <img style={{ width: '50%' }} src="https://sejutakg.datapassport.com/static/media/sejutakg-logo.177432c0.svg" alt="SejutaKG Logo" />
        <Typography>Thank you for signing up as a Merchant for SejutaKG!</Typography>
        <Typography>Why not <a href="/offers/create">create your first offer?</a></Typography>
      </CardContent>
      <CardActions>
        <Button onClick={closeModalWindow}>Close</Button>
      </CardActions>
    </Card>
  );
};
